/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectionManagementWizard/useInvoiceReasonCodeData"/>
import { ListItem, SortInfo } from "@medius/ui-controls";
import { useEffect, useMemo, useState } from "react";
import { getActiveRejectionActions, getActiveRejectionManagementReasonCodes, getMergedCompanyConfiguration } from "./services";
import { IReasonCode, IInvoiceReasonCodeData, CompanyConfigurationFields, RejectionManagementCompanyConfiguration, RejectOperation } from "./interfaces";
import { searchRejectionManagementReasonCodes } from "./searching";
import { ReasonCodeOperationType } from "../shared/interfaces";

export const useInvoiceReasonCodeData = (operation : ReasonCodeOperationType, hierarchyId: string, rejectOperation?: RejectOperation): IInvoiceReasonCodeData => {
    const [sortInfo, setSortInfo] = useState<SortInfo | null>(null);
    const [{ page, pageSize }, setPageState] = useState({
        page: 0,
        pageSize: 25
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [totalInvoiceReasonCodeCount, setTotalInvoiceReasonCodeCount] = useState(0);

    const changePageSize = (newPageSize: number) => {
        setPageState((prev) => ({
            ...prev,
            pageSize: newPageSize
        }));
    };

    const changePage = (newPage: number) => {
        setPageState((prev) => ({
            ...prev,
            page: newPage
        }));
    };

    const getOperationName = (type: ReasonCodeOperationType) => {
        switch(type) {
            case ReasonCodeOperationType.Delete:
                return 'Delete';
            case ReasonCodeOperationType.Invalidate:
            case ReasonCodeOperationType.InvalidateAndEdit:
                return 'Invalidate';
            case ReasonCodeOperationType.PutOnHold:
                return 'OnHold';
            case ReasonCodeOperationType.RejectDeviation:
                return 'Reject';
            default:
                return null;
        }
    };

    const [allData, setAllData] = useState<IReasonCode[]>([]);
    const [actionOptions, setActionOptions] = useState<ListItem[]>([]);
    const [companyConfiguration, setCompanyConfiguration] = useState<RejectionManagementCompanyConfiguration>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        getActiveRejectionManagementReasonCodes(hierarchyId)
            .then((result) => {
                let filtered = [
                    ...result.filter(r => {
                        const op = getOperationName(operation);
                        const matchesOperation = r.operations?.includes(op) || r.operations?.includes("All");

                        if (!matchesOperation) return false;

                        if (op === "Reject" && rejectOperation) {
                            return r.rejectOperation === rejectOperation;
                        }

                        return true;
                    })
                ];
                // Move 'Other' with isSystemReasonCode true to the end
                const others = filtered.filter(r => r.reasonCode?.trim().toLowerCase() === 'other' && r.isSystemReasonCode === true);
                filtered = filtered.filter(r => !(r.reasonCode?.trim().toLowerCase() === 'other' && r.isSystemReasonCode === true));
                filtered = [...filtered, ...others];
                setAllData(filtered);
                setIsError(false);
            })
            .catch(() => {
                setAllData([]);
                setIsError(true);
            })
            .finally(() => setIsLoading(false));

        getActiveRejectionActions(hierarchyId)
            .then((result) => { 
                setActionOptions(result.map(a => ({ 
                    value: String(a.id), 
                    header: a.code, 
                    text: a.name, 
                    subText: a.description})));
                setIsError(false);
            })
            .catch(() => {
                setActionOptions([]);
                setIsError(true);
            })
            .finally(() => setIsLoading(false));

        const companyId = getCompanyIdByHierarchyId(hierarchyId);
        if(companyId === null){
            setIsError(true);
        }

        getMergedCompanyConfiguration(companyId, [
            CompanyConfigurationFields.ReasonRequiredForDeletionAndInvalidation,
            CompanyConfigurationFields.SupplierEmailForRejections
        ])
            .then((result) => {
                const mappedResult: RejectionManagementCompanyConfiguration = {
                    companyId: companyId,
                    reasonRequiredForDeletionAndInvalidation: result.reasonRequiredForDeletionAndInvalidation,
                    supplierEmailForRejections: result.supplierEmailForRejections
                };
                setCompanyConfiguration(mappedResult);
                setIsError(false);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => setIsLoading(false));

    }, [operation, hierarchyId]);

    const pagedData = useMemo(() => {
    const data = [...searchRejectionManagementReasonCodes(allData, searchTerm)];
    setTotalInvoiceReasonCodeCount(data.length);
    
    if (sortInfo?.columnKey) {
        const key = sortInfo.columnKey as keyof IReasonCode;

        data.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === bValue) return 0;

        const isAsc = sortInfo.direction === "ascending";
        return aValue > bValue ? (isAsc ? 1 : -1) : (isAsc ? -1 : 1);
        });
    }

    const start = page * pageSize;
    return data.slice(start, start + pageSize);
    }, [allData, page, pageSize, sortInfo, searchTerm]);

    return {
        invoiceReasonCodes: pagedData,
        companyConfiguration: companyConfiguration,
        totalInvoiceReasonCodeCount: totalInvoiceReasonCodeCount,
        actions: actionOptions,
        isLoading: isLoading,
        isError: isError,
        paging: {
            currentPage: page,
            pageSize: pageSize,
            changePage: changePage,
            changePageSize: changePageSize
        },
        sorting: {
            sortInfo: sortInfo,
            changeSortInfo: setSortInfo
        },
        searching: {
            changeSearchTerm: (term: string) =>{
                changePage(0);
                setSearchTerm(term);
            }
        }
    };
};

const getCompanyIdByHierarchyId = (hierarchyId: string): number | null => {
    const parts = hierarchyId.split("/").filter(Boolean);
    const last = parts.pop();
    const id = Number(last);
    return isNaN(id) ? null : id;
};